import { useMemo, useState, useCallback } from 'react';
import type React from 'react';
import useMuiTableBuilderV2 from 'hooks/useMuiTableBuilderV2';
import getTableConfig from './tableConfig';
import { Box, TablePaginationProps } from '@mui/material';
import { MembersV2CustomRowProps } from './types';
import TeamsPaginationFooter from 'TeamModule/components/TeamsV2Page/components/TeamsPaginationFooter';
import { useMembersV2ListItemBuilder } from './useMembersV2ListItemBuilder';
import { MemberSearchItem } from './types';
import { Button } from '@mui/material';
import { useMembersV2BulkActionMenu } from './useMembersV2BulkActionMenu';
import type { MemberImpersonationItem } from 'TeamModule/api/membersImpersonationApi';

const tableConfig = getTableConfig();

export const MembersV2Table = ({
  members,
  isLoading,
  selectedIds,
  hasSelectionOnPage,
  allOnPageSelected,
  isIndeterminateOnPage,
  onToggleRow,
  onToggleSelectAll,
  paginationProps,
  orderBy,
  orderDirection,
  onSort,
  impersonationMap,
  isLoadingImpersonation,
  isImpersonationError,
}: {
  members: MemberSearchItem[];
  isLoading: boolean;
  selectedIds: Set<string>;
  hasSelectionOnPage: boolean;
  allOnPageSelected: boolean;
  isIndeterminateOnPage: boolean;
  onToggleRow: (id: string) => void;
  onToggleSelectAll: (checked: boolean) => void;
  paginationProps: TablePaginationProps & { indexBase?: 0 | 1 };
  orderBy: string;
  orderDirection: MembersV2CustomRowProps['orderDirection'];
  onSort: MembersV2CustomRowProps['onSort'];
  impersonationMap: Map<string, MemberImpersonationItem>;
  isLoadingImpersonation: boolean;
  isImpersonationError: boolean;
}) => {
  const listItemsBuilder = useMembersV2ListItemBuilder();
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());
  const onToggleExpand = useCallback((id: string) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);
  const mainList = useMemo(() => {
    return {
      listItems: listItemsBuilder({
        members: members.map((m) => ({ ...m })),
        expandedIds,
        impersonationMap,
        isLoadingImpersonation,
        isImpersonationError,
      }),
    };
  }, [
    listItemsBuilder,
    members,
    expandedIds,
    impersonationMap,
    isLoadingImpersonation,
    isImpersonationError,
  ]);

  const _customRowProps = useMemo(
    () => ({
      selectedIds,
      onToggleRow,
      allOnPageSelected,
      isIndeterminateOnPage,
      onToggleSelectAll,
      orderBy,
      orderDirection,
      onSort,
      expandedIds,
      onToggleExpand,
    }),
    [
      allOnPageSelected,
      isIndeterminateOnPage,
      onSort,
      onToggleRow,
      onToggleSelectAll,
      orderBy,
      orderDirection,
      selectedIds,
      expandedIds,
      onToggleExpand,
    ],
  );

  const renderFooterOptions = () => <TeamsPaginationFooter paginationProps={paginationProps} />;

  const { openMembersV2BulkActionMenu, MembersV2BulkActionMenuComponent } =
    useMembersV2BulkActionMenu();

  const renderHeaderTools = () => {
    const selectedMembers: MemberSearchItem[] = members.filter((m) => selectedIds.has(m.id));
    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          ml: 'auto',
        }}
      >
        <Button
          variant="outlined"
          color="primary"
          onClick={(e: React.MouseEvent<HTMLButtonElement>) =>
            openMembersV2BulkActionMenu({
              target: e.currentTarget as HTMLElement,
              members: selectedMembers,
              impersonationMap,
            })
          }
        >
          Actions
        </Button>
        {MembersV2BulkActionMenuComponent}
      </Box>
    );
  };

  const { TableComponent } = useMuiTableBuilderV2({
    ...tableConfig,
    mainList,
    _customRowProps,
    isLoading,
    renderFooterOptions,
    renderHeaderTools,
    tableMaxHeight: '800px',
  });

  return <Box>{TableComponent}</Box>;
};

export default MembersV2Table;
